import { Chip, Divider } from '@mui/material';
import { Button, Dialog, DialogContent, Grid, Typography, TextField, ButtonGroup } from '@mui/material';
import IconButton from 'components/@extended/IconButton';
import { PopupTransition } from 'components/@extended/Transitions';
import { CloseCircle } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { Autocomplete } from '@mui/material';
import { OrderType } from 'enum/order-type';
import { InputLabel } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { FormHelperText } from '@mui/material';
import OrderPlacedAlert from 'components/alert/OrderPlacedAlert';
import placeOrderValidationSchema from 'validations/placeOrderValidationSchema';
import serverCall from 'api/serverCall';
import api from 'api/api';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useDataContext } from 'contexts/DataContext';
import { IOrderV2 } from 'types/custom/order';
import { TransactionType } from 'enum/transaction-type';
import { Limit } from 'types/custom/common';
import { MarketQuote } from 'types/custom/market';
import { ListboxComponent } from './ListboxCompoenet';
import { LinearProgress } from '@mui/material';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import { Tick } from 'types/custom/websocket';

interface Props {
  open: boolean;
  handleClose: (status: boolean) => void;
  order: IOrderV2 | null;
  orderType: OrderType | null;
  action: string;
  fetchAllOrders: () => void;
}

type AlertData = {
  open: boolean;
  status: 'success' | 'failed' | '';
  total: number;
  success: number;
  failed: number;
};

export default function OrderAgainModalV2({ open, handleClose, order, orderType, action, fetchAllOrders }: Props) {
  const [isSlEnabled, setIsSlEnabled] = useState<boolean>(orderType === OrderType.SL);
  const [orderAlertState, setOrderAlertState] = useState<AlertData>({ open: false, status: '', total: 0, success: 0, failed: 0 });
  const [limit, setLimit] = useState<Limit | null>(null);
  const [marketQuoteLoading, setMarketQuoteLoading] = useState<boolean>(false);
  const [currentTick, setCurrentTick] = useState<Tick | null>(null);
  const { liveTicks } = useWebsocketContext();
  const { stocks } = useDataContext();

  const userId: string = order?.userId as string;
  const formik = useFormik({
    initialValues: {
      stock: order?.stockId,
      price: order?.price ?? '',
      orderType: orderType,
      quantity: order?.quantity ?? '',
      triggerPrice: order?.triggerPrice ?? '',
      transactionType: '',
      submit: null
    },
    validationSchema: placeOrderValidationSchema(isSlEnabled, limit),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      try {
        if (!userId) {
          return;
        }

        if (values?.transactionType === 'BUY' && values.orderType === OrderType.SL && values?.price < values?.triggerPrice) {
          setFieldError('triggerPrice', 'Trigger price must be less than or equal to the price for BUY orders');
          return;
        }

        if (values?.transactionType === 'SELL' && values.orderType === OrderType.SL && values?.price > values?.triggerPrice) {
          setFieldError('triggerPrice', 'Trigger price must be greater than or equal to the price for SELL orders');
          return;
        }
        const isLoggedIn = await getLoginStatus(userId);
        if (!isLoggedIn) {
          const fullName = order?.firstName + ' ' + order?.lastName;
          const placedOrderType = orderType === OrderType.LIMIT ? 'Limit Order' : orderType === OrderType.SL ? 'Stop Loss Order' : 'Order';
          const errorMessage = `The user   ${fullName}  must be logged in to place a ${placedOrderType} `;
          dispatch(
            openSnackbar({
              open: true,
              close: true,
              message: errorMessage,
              variant: 'alert',
              alert: {
                color: 'success'
              },
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
              }
            })
          );
          return;
        }
        handleCloseModal();
        const requestBody = [
          {
            userId: userId,
            requestId: order?.requestId ?? 0,
            orderId: action == 'oa' || action == 'to' || action == 'sl' ? 0 : order?.orderId,
            quantity: values.quantity,
            stockId: order?.stockId,
            transactionType:
              action == 'to' || action === 'sl'
                ? TransactionType.SELL
                : orderType === OrderType.REPEAT_ORDER
                ? values?.transactionType
                : order?.transactionType,
            price: values.price,
            orderType: orderType === OrderType.REPEAT_ORDER ? OrderType.LIMIT : orderType,
            validity: 'DAY',
            product: 'CNC',
            triggerPrice: orderType === OrderType.LIMIT || orderType === OrderType.REPEAT_ORDER ? values.price : values.triggerPrice
          }
        ];
        serverCall.post(api.placeOrder(), requestBody, (response: any, error: any) => {
          if (error) {
            console.error(error);
            const errorMessage = error?.response?.data.message as string;
            setSubmitting(false);
            dispatch(
              openSnackbar({
                open: true,
                close: true,
                message: errorMessage || `Failed to place order, please try again later`,
                variant: 'alert',
                alert: {
                  color: 'error'
                },
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center'
                }
              })
            );
          } else {
            const orderList = response?.orderList;
            if (orderList.length > 0) fetchAllOrders();
            const fullName = order?.firstName + ' ' + order?.lastName;
            const placedOrderType =
              orderType === OrderType.LIMIT ? 'Limit Order' : orderType === OrderType.SL ? 'Stop Loss Order' : 'Order';
            orderList && orderList.length > 0
              ? dispatch(
                  openSnackbar({
                    open: true,
                    close: true,
                    message: `Successfully placed  a ${placedOrderType} for ${fullName}`,
                    variant: 'alert',
                    alert: {
                      color: 'success'
                    },
                    anchorOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    }
                  })
                )
              : dispatch(
                  openSnackbar({
                    open: true,
                    close: true,
                    message: `Failed to place a ${placedOrderType} for ${fullName}, Please try again later`,
                    variant: 'alert',
                    alert: {
                      color: 'error'
                    },
                    anchorOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    }
                  })
                );
          }
        });
        setSubmitting(false);
        resetForm();
        setLimit(null);
      } catch (error) {
        console.error(error);
      }
    }
  });

  const getLoginStatus = (userId: string): Promise<boolean> => {
    return new Promise((res, rej) => {
      serverCall.get(api.checkUserLoginStatus(userId), (response: any, error: any) => {
        if (error) {
          rej(false);
        } else {
          res(response?.status);
        }
      });
    });
  };

  const getPriceCircuit = (stockId: string) => {
    setMarketQuoteLoading(true);
    serverCall.get(api.getMarketQuote(stockId), (response: MarketQuote, error: any) => {
      if (error) {
        console.error(error);
        setMarketQuoteLoading(false);
      } else {
        const limit = {
          upperLimit: response?.upperCircuitLimit,
          lowerLimit: response?.lowerCircuitLimit,
          lastPrice: response?.lastPrice,
          stockId
        };
        setLimit(limit);
        setMarketQuoteLoading(false);
      }
    });
  };

  useEffect(() => {
    formik.setValues({
      stock: order?.stockId,
      price: order?.price ?? '',
      orderType: orderType,
      quantity: order?.quantity ?? '',
      triggerPrice: order?.triggerPrice ?? '',
      transactionType: '',
      submit: null
    });
    setIsSlEnabled(orderType === OrderType.SL);
  }, [order, orderType]);

  useEffect(() => {
    if (order?.stockId) {
      getPriceCircuit(order?.stockId);
    }
  }, [order?.stockId]);

  useEffect(() => {
    if (liveTicks && order?.stockId) {
      const tick = liveTicks.current[order?.stockId];
      setCurrentTick(tick);
    }
  }, [order]);

  function handleCloseModal() {
    handleClose(false);
  }

  function handleCloseModalAndClearData() {
    if (formik) {
      formik.resetForm();
    }
    setLimit(null);
    handleClose(true);
  }

  const handleCloseOrderPlacedAlert = () => {
    setOrderAlertState({ open: false, status: '', total: 0, success: 0, failed: 0 });
  };

  const getTitle = (action: string) => {
    switch (action) {
      case 'sl':
        return 'Place Stop Loss Order';
      case 'to':
        return 'Place Target Order';
      case 'oa':
        return 'Order Again';
      case 'mo':
        return 'Modify Order';
    }
  };
  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, values, handleChange, setFieldValue } = formik;

  const getBuySellButtons = (action: string, transactionType: TransactionType) => {
    const isFlipAction = ['sl', 'to'].includes(action);
    const shouldFlip = isFlipAction;

    const newTransactionType = shouldFlip
      ? transactionType === TransactionType.SELL
        ? TransactionType.BUY
        : TransactionType.SELL
      : transactionType;

    const isBuyButton = newTransactionType === TransactionType.BUY;

    return (
      <Button
        size="large"
        type="button"
        variant="contained"
        onClick={async () => {
          await setFieldValue('transactionType', newTransactionType);
          await formik.submitForm();
        }}
        sx={isBuyButton ? { mr: 2 } : undefined}
        color={!isBuyButton ? 'error' : undefined}
        disabled={isSubmitting}
      >
        {isBuyButton ? 'BUY' : 'SELL'}
      </Button>
    );
  };

  return (
    <>
      <OrderPlacedAlert
        open={orderAlertState.open}
        handleClose={handleCloseOrderPlacedAlert}
        status={orderAlertState.status}
        totalOrders={orderAlertState.total}
        successFulOrders={orderAlertState.success}
      />
      <Dialog
        open={open}
        onClose={handleCloseModalAndClearData}
        keepMounted
        fullWidth
        TransitionComponent={PopupTransition}
        maxWidth="md"
        aria-labelledby="place-order-confirmation-title"
        aria-describedby="place-order-confirmation-description"
      >
        <Grid
          container
          spacing={0}
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderBottom: '1px solid {theme.palette.divider}', p: 2 }}
        >
          <Typography variant="h4" sx={{ ml: 1 }}>
            {`${getTitle(action)} - ${order?.firstName} ${order?.lastName} - ${order?.broker}`}
          </Typography>
          <IconButton color="secondary" onClick={handleCloseModal}>
            <CloseCircle />
          </IconButton>
          <Grid container spacing={2} justifyContent={'space-between'}></Grid>
        </Grid>
        <Divider />
        <DialogContent sx={{ m: 0, p: 2.5, py: 3 }}>
          <>
            {(currentTick || limit?.lastPrice) && (
              <Grid item xs={12} md={12} sx={{ margin: 0, padding: 0 }}>
                <Chip
                  size="small"
                  label={
                    <Typography color={'#808080'} variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {` NSE - ${limit?.stockId} :`}
                      {marketQuoteLoading ? (
                        <LinearProgress color="primary" style={{ width: '2rem' }} />
                      ) : (
                        <span style={{ marginLeft: '2px' }}>{currentTick?.price ?? limit?.lastPrice}</span>
                      )}
                    </Typography>
                  }
                  sx={{ borderRadius: 1, p: 0, backgroundColor: 'transparent', mt: -1, ml: -0.9 }}
                />
              </Grid>
            )}
            <Grid sx={{ mt: 2 }}>{''}</Grid>
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="stock-label" sx={{ mb: 1 }}>
                      Stock
                    </InputLabel>
                    <Autocomplete
                      disabled
                      size="medium"
                      disablePortal
                      fullWidth
                      id="basic-autocomplete-label"
                      options={stocks.map((item) => item?.stockId)}
                      ListboxComponent={ListboxComponent}
                      isOptionEqualToValue={(option, value) => option === value || (value === '' && option === undefined)}
                      value={values?.stock ?? null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select stock"
                          inputProps={{
                            ...params.inputProps
                          }}
                        />
                      )}
                      noOptionsText="No stocks found"
                    />

                    {touched.stock && errors.stock && (
                      <FormHelperText error id="standard-weight-helper-text-stock-place-order">
                        {errors.stock}
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="order-type-label" sx={{ mb: 1 }}>
                      Order Type
                    </InputLabel>
                    <ButtonGroup fullWidth sx={{ height: 48 }} variant="outlined" aria-label="Loading button group">
                      <Button variant={orderType === OrderType.LIMIT || orderType === OrderType.REPEAT_ORDER ? 'contained' : 'outlined'}>
                        Regular
                      </Button>
                      <Button
                        onClick={() => {
                          setIsSlEnabled(true);
                        }}
                        variant={orderType === OrderType.SL ? 'contained' : 'outlined'}
                      >
                        Stop Loss
                      </Button>
                    </ButtonGroup>
                    {touched.orderType && errors.orderType && (
                      <FormHelperText error id="standard-weight-helper-text-order-type-place-order">
                        {errors.orderType}
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="price-label" sx={{ mb: 1 }}>
                      Price
                    </InputLabel>
                    <TextField
                      inputProps={{ min: 0, step: 0.1 }}
                      id="outlined-number"
                      placeholder="Price"
                      type="number"
                      {...getFieldProps('price')}
                      onChange={handleChange}
                      fullWidth
                    />
                    {touched.price && errors.price && (
                      <FormHelperText error id="standard-weight-helper-text-price-place-order">
                        {errors.price}
                      </FormHelperText>
                    )}
                  </Grid>
                  {orderType === OrderType.SL && (
                    <Grid item xs={12} md={6}>
                      <InputLabel htmlFor="trigger-price-label" sx={{ mb: 1 }}>
                        Trigger Price
                      </InputLabel>
                      <TextField
                        inputProps={{ min: 1 }}
                        id="outlined-number"
                        placeholder="Trigger Price"
                        type="number"
                        {...getFieldProps('triggerPrice')}
                        onChange={handleChange}
                        fullWidth
                      />
                      {touched.triggerPrice && errors.triggerPrice && (
                        <FormHelperText error id="standard-weight-helper-text-trigger-price-place-order">
                          {errors.triggerPrice}
                        </FormHelperText>
                      )}
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="quantity-label" sx={{ mb: 1 }}>
                      Quantity
                    </InputLabel>
                    <TextField
                      inputProps={{ min: 1 }}
                      id="outlined-number"
                      placeholder="Quantity"
                      type="number"
                      {...getFieldProps('quantity')}
                      onChange={handleChange}
                      fullWidth
                    />
                    {touched.quantity && errors.quantity && (
                      <FormHelperText error id="standard-weight-helper-text-quantity-place-order">
                        {errors.quantity}
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Grid container justifyContent="flex-end">
                      {/* {orderType === OrderType.REPEAT_ORDER ? (
                        <>
                          <Button
                            size="large"
                            sx={{ mr: 2 }}
                            onClick={async () => {
                              await setFieldValue('transactionType', TransactionType.BUY);
                              await formik.submitForm();
                            }}
                            type="button"
                            variant="contained"
                            disabled={isSubmitting}
                          >
                            BUY
                          </Button>
                          <Button
                            onClick={async () => {
                              await setFieldValue('transactionType', TransactionType.SELL);
                              await formik.submitForm();
                            }}
                            size="large"
                            type="button"
                            variant="contained"
                            color="error"
                            disabled={isSubmitting}
                          >
                            SELL
                          </Button>
                        </>
                      ) : (
                        <>
                          {order && order?.transactionType === 'SELL' ? (
                            <Button
                              size="large"
                              onClick={async () => {
                                await setFieldValue('transactionType', TransactionType.SELL);
                                await formik.submitForm();
                              }}
                              type="button"
                              variant="contained"
                              color="error"
                              disabled={isSubmitting}
                            >
                              SELL
                            </Button>
                          ) : (
                            <Button
                              size="large"
                              sx={{ mr: 2, width: '10rem' }}
                              onClick={async () => {
                                await setFieldValue('transactionType', TransactionType.BUY);
                                await formik.submitForm();
                              }}
                              type="button"
                              variant="contained"
                              disabled={isSubmitting}
                            >
                              BUY
                            </Button>
                          )}
                        </>
                      )} */}
                      {getBuySellButtons(action, order?.transactionType as TransactionType)}
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </>
        </DialogContent>
      </Dialog>
    </>
  );
}
